(function () {
  "use strict";

  const awtomicWindow = /** @type {Window & { Awtomic?: any, reactInit?: any, AWT_DEBUG?: boolean }} */ (window);

  // Feature flags -----------------------------------------------------------
  // When true, editing (cp-edit-hidden) is gated by the release/customization
  // window as before. When false, the customization-window checks are skipped
  // entirely, so editing is no longer blocked by being outside the window
  // (other gates like recently-billed still apply).
  const useCustomizationWindow = true;
  const customizationStartTimePT = "7:00";
  const customizationEndTimePT = "7:00";
  // When true, a PICKUP member is treated as non-editable. HALL now wants
  // pickup members to customize inside their own window, so this is off.
  const disablePickupEdit = false;

  // CX-5056 failsafe global (orden 2031): con true, ninguna sub es editable ni
  // en la lista ni en el detalle del portal, sin mirar ventanas, ids ni tags.
  // Queda en false: la ventana q4 es la que manda. Es el kill switch de una linea.
  const disableCustomization = false;

  // Detect a PICKUP member from the CUSTOMER TAGS, not from DeliveryMethod.
  // Pickup delivery methods were removed from every customer and replaced with
  // a "Pickup" tag, so every subscription now reads as Shipping regardless of
  // how the member actually collects the wine. The tag list is rendered by
  // snippets/awtomic-theme-custom.liquid into window.AWT_CUSTOMER_TAGS, and it
  // is an empty list for a logged-out visitor.
  const PICKUP_TAG = "pickup";
  const isPickupMember = () => {
    const tags = awtomicWindow.AWT_CUSTOMER_TAGS;
    if (!Array.isArray(tags)) return false;
    return tags.some((tag) => String(tag).trim().toLowerCase() === PICKUP_TAG);
  };

  // Pick the date set that applies to THIS member. Every entry in
  // releaseWindowData carries both, and the two differ per club.
  /** @param {any} data @param {boolean} multiBundle */
  const releaseWindowDatesFor = (data, multiBundle) =>
    isPickupMember() ? data.Pickup.date : multiBundle ? multiBundleWindowDates : data.Shipping.date;

  /** @param {string | number} productId */
  const getReleaseWindowFromProductId = (productId) => {
    const productIdString = String(productId);
    for (const [bundleId, data] of releaseWindowEntries) {
      if (productIdString.includes(bundleId)) {
        return data;
      }
    }
    return null;
  };

  // CX-4775 — products that exempt a subscription from the cp-edit release-window block.
  // If any line's ProductId matches one of these, the sub bypasses ALL cp-edit-block rules
  // (release window + recently-billed). Substring-matched, same as getReleaseWindowFromProductId.
  const editBlockExceptionBundleIds = [
    "10543715877166", // Wild Test Club
    "10568341291310" // Wild Test Club 2
  ];
  /** @param {string | number} productId */
  const isEditBlockExceptionProductId = (productId) => {
    const s = String(productId);
    return editBlockExceptionBundleIds.some((id) => s.includes(String(id)));
  };

  // --- Port of the app's own "can this line be edited?" rules -----------------
  // The subs-list Customize button hands off to the CP's "Edit my box" action, so
  // it must only render where the app will actually offer that action. All of
  // these inputs are line CustomAttributes, which the list already has (it loads
  // lines with expandLines=true), so no extra request is needed.
  // Mirrors: bundle-shopify/lib/scLineMods.ts + widgetSrc/subscriptions/
  // state/useProductContext.tsx (the edit_my_box menu-option conditions).

  /** @param {any} line @param {string} key */
  const getLineAttr = (line, key) => line?.CustomAttributes?.find((attr) => attr.key === key)?.value;

  // scLineMods bit positions, read right-to-left; "1" means NOT allowed.
  // EDIT_BUNDLE gates the "Edit my box" menu entry; SWAP_VARIANT gates whether
  // the options cell renders at all for a box line (via
  // shouldDisableChangeProductOptions -> hideEditMyBoxOptions). Both matter.
  const SC_LINE_MOD_EDIT_BUNDLE = 0;
  const SC_LINE_MOD_SWAP_VARIANT = 3;
  /** @param {any} line @param {number} mod */
  const scLineModAllowed = (line, mod) => {
    const modValue = getLineAttr(line, "_awtMods") || "";
    return (modValue[modValue.length - 1 - mod] || "0") !== "1";
  };

  // The widget hydrates its whole GlobalContext from window.reactInit
  // (widgetSrc/subscriptions/index.tsx:47,64), and that same script mounts on the
  // subs list — so the shop-level portal settings the CP gates on are readable
  // here with no request.
  const PRODUCT_CHANGE_NOT_ALLOWED = "NOT_ALLOWED";
  const getCustomerPortalSettings = () => awtomicWindow.reactInit?.customerPortalSettings;
  const productChangeNotAllowed = () => getCustomerPortalSettings()?.productChange === PRODUCT_CHANGE_NOT_ALLOWED;

  // A line is a gift only when ALL four gift attributes are present.
  const GIFT_ATTRIBUTE_KEYS = ["_giftFirstName", "_giftLastName", "_giftEmail", "_giftNote"];
  /** @param {any} line */
  const isGiftLine = (line) => GIFT_ATTRIBUTE_KEYS.every((key) => line?.CustomAttributes?.some((attr) => attr.key === key));

  // preventEditFreeItems is a shop-level customerPortalSettings value that the
  // widget never puts on window, so we can't read it. Fall back to the app's own
  // undefined-branch behaviour: a zero-price line counts as free.
  /** @param {any} line */
  const isFreeLine = (line) => line?.CurrentPriceAmount === "0.0" && line?.PrepaidVersion !== 3;

  // True when the app would render an "Edit my box" action for this line.
  // Mirrors useProductContext.tsx:148-156 (menu entry) AND the TableRow.tsx
  // conditions that decide whether the options cell renders at all for a box:
  //   :824  hideEditMyBoxOptions = shouldDisableChangeProductOptions && isBaB
  //   :721  shouldDisableChangeProductOptions = isGift || currencyDiff
  //           || productChange === NOT_ALLOWED || !scLineModAllowed(SWAP_VARIANT)
  //           || (statusNotOk && !isBaB)
  // The statusNotOk clause is dead for a box line (isBaB is true), so ProductStatus
  // is not needed — which is just as well, since the list payload omits it.
  // currencyDiff is not readable here; it only turns true after a ?locred= market
  // redirect, so treating it as false matches the normal case.
  /** @param {any} line */
  const lineOffersEditMyBox = (line) => {
    const bundleType = getLineAttr(line, "_bundleType");
    if (!getLineAttr(line, "_bundleProductId")) return false; // not a box
    if (getLineAttr(line, "_addon")) return false;
    if (bundleType === "shuffle" || bundleType === "classic") return false;
    if (isFreeLine(line)) return false;
    if (isGiftLine(line)) return false;
    if (productChangeNotAllowed()) return false; // shop-level admin setting
    if (!scLineModAllowed(line, SC_LINE_MOD_SWAP_VARIANT)) return false; // hides the whole cell
    if (line?.PrepaidV2) return false; // needs hasPrepaidV2SwapEnabled, not readable — fail closed
    return scLineModAllowed(line, SC_LINE_MOD_EDIT_BUNDLE); // admin block via _awtMods
  };

  // A subscription is "on hold" when the merchant's Hold Flag is set, or when
  // OriginalNextBillingDate is set. Those are two different Awtomic concepts and
  // both must hide Customize:
  //   - HoldFlag: the merchant-set hold (CX-5123). This is the one HALL means by
  //     "on hold"; 654 ACTIVE HALL contracts carry it today.
  //   - OriginalNextBillingDate: the inventory-delay hold. The merchant action
  //     that lifts it is
  //     clearSubscriptionHoldStatusHandler.js, which does exactly one thing —
  //     removeOriginalNextBillingDateFromContract. The admin badges it via
  //     getBadgeStatus (components/Subscriptions/constants.js:92), and the CP uses
  //     the same field to disable Order Now (SubsDetail.tsx:262-266).
  // Both fields live on SubscriptionContract, and the list/detail endpoints return
  // the whole contract (GSI2-1 is ProjectionType: ALL, no ProjectionExpression), so
  // both are in the list payload too.
  // NOTE: hold does NOT suppress the CP's edit box — this is our own product
  // rule (don't offer Customize on a held subscription), not app parity.
  /** @param {any} subscription */
  const isSubscriptionOnHold = (subscription) =>
    !!subscription?.HoldFlag || !!subscription?.OriginalNextBillingDate;

  // Show Customize only on a genuinely live subscription.
  //
  // RULE: must be ACTIVE and not on hold. SubscriptionStatus covers CANCELLED /
  // PAUSED / FAILED / EXPIRED; a held sub keeps SubscriptionStatus === "ACTIVE",
  // so it needs the separate check above.
  //
  // The last two mirror the app's own render conditions (TableRow.tsx:288-296,
  // 913) so the list never advertises an edit the CP won't offer. Note that
  // "prepaid" there is a billing-vs-delivery interval mismatch, NOT the
  // PrepaidV2/PrepaidVersion fields — MONTH#3 vs MONTH#3 is not prepaid.
  // All of these only ever make the button rarer, never more common.
  /** @param {any} subscription */
  const subscriptionAllowsBoxEdit = (subscription) => {
    if (!subscription) return { ok: false, reason: "no subscription" };

    // --- the rule ---
    if (subscription.SubscriptionStatus !== "ACTIVE") {
      return { ok: false, reason: `not ACTIVE (${subscription.SubscriptionStatus})` };
    }
    if (isSubscriptionOnHold(subscription)) {
      return {
        ok: false,
        reason: `on hold (HoldFlag = ${subscription.HoldFlag}, OriginalNextBillingDate = ${subscription.OriginalNextBillingDate})`
      };
    }

    // --- mirroring the app's render gate ---
    const b = subscription.BillingIntervalBillingCount;
    const d = subscription.DeliveryIntervalDeliveryCount;
    if (!!b && !!d && b != d) return { ok: false, reason: `prepaid (billing ${b} != delivery ${d})` };
    if (subscription.IsBilling || subscription.IsUpdating || subscription.IsBillingScheduled) {
      return { ok: false, reason: "billing/updating/scheduled" };
    }
    return { ok: true, reason: "" };
  };

  // Opt-in tracing for the edit/Customize gating. Off for customers; the subs-list
  // gate requires an empty query string so a ?debug= param can't be used here.
  // Switch on with localStorage.setItem("awtDebug", "1") (or window.AWT_DEBUG = true
  // before this script loads) and reload.
  const awtDebug = (() => {
    try {
      return localStorage.getItem("awtDebug") === "1" || awtomicWindow.AWT_DEBUG === true;
    } catch (_e) {
      return awtomicWindow.AWT_DEBUG === true;
    }
  })();
  /** @param {...any} args */
  const awtLog = (...args) => {
    if (awtDebug) console.log("[awt-edit-gate]", ...args);
  };

  /** @type {Record<string, { brand: string, bundleName: string, Shipping: { date: { customizationStarts: string, customizationEnds: string, billingDate: string, shippingDate: string } }, Pickup: { date: { customizationStarts: string, customizationEnds: string, billingDate: string, shippingDate: string } } }>} */
  const releaseWindowData = {
    10541219676462: {
      brand: "HALL",
      bundleName: "Primary Colors",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-19",
          billingDate: "10-19",
          shippingDate: "10-28"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    },
    10511307702574: {
      brand: "HALL",
      bundleName: "Collection",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-21",
          billingDate: "10-21",
          shippingDate: "11-02"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    },
    10542471774510: {
      brand: "WALT",
      bundleName: "Vintner's",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-26",
          billingDate: "10-28",
          shippingDate: "11-04"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    },
    10542568210734: {
      brand: "WALT",
      bundleName: "Appellation",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-26",
          billingDate: "10-28",
          shippingDate: "11-04"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    },
    10542553497902: {
      brand: "BACA",
      bundleName: "BACA",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-26",
          billingDate: "10-28",
          shippingDate: "11-04"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    },
    10543703589166: {
      brand: "Michel Foch",
      bundleName: "Michel Foch",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-26",
          billingDate: "10-28",
          shippingDate: "11-04"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    },
    10542545797422: {
      brand: "Mixed (Duals/Hybrid)",
      bundleName: "Custom",
      Shipping: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-21",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      },
      Pickup: {
        date: {
          customizationStarts: "09-21",
          customizationEnds: "10-28",
          billingDate: "10-28",
          shippingDate: "11-02"
        }
      }
    }
  };

  const releaseWindowEntries = Object.entries(releaseWindowData);

  const multiBundleWindowDates = { customizationStarts: "09-21", customizationEnds: "10-28" };

  /** @param {any[] | undefined} lines */
  const hasMultipleReleaseWindowBundles = (lines) =>
    new Set(
      (lines || [])
        .map((line) => getLineAttr(line, "_bundleProductId"))
        .filter((id) => id && getReleaseWindowFromProductId(id))
        .map(String)
    ).size >= 2;

  const ptFormat = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
  });

  /** @param {string} time "H:MM" */
  const parseTime = (time) => {
    const parts = String(time).split(":");
    return { hour: Number(parts[0]), minute: Number(parts[1] || 0) };
  };

  /** @param {number} year @param {number} month @param {number} day @param {string} time */
  const zonedDate = (year, month, day, time) => {
    const t = parseTime(time);
    const target = Date.UTC(year, month - 1, day, t.hour, t.minute);
    let guess = target;
    for (let i = 0; i < 2; i++) {
      const p = Object.fromEntries(ptFormat.formatToParts(new Date(guess)).map((x) => [x.type, Number(x.value)]));
      guess += target - Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    }
    return new Date(guess);
  };

  /** @param {string} monthDay @param {number} year */
  const parseMonthDayStart = (monthDay, year) => {
    const parts = String(monthDay).split("-");
    const month = Number(parts[0]);
    const day = Number(parts[1]);
    if (!Number.isFinite(month) || !Number.isFinite(day)) return new Date(NaN);
    return zonedDate(year, month, day, customizationStartTimePT);
  };

  /** @param {string} monthDay @param {number} year */
  const parseMonthDayEnd = (monthDay, year) => {
    const parts = String(monthDay).split("-");
    const month = Number(parts[0]);
    const day = Number(parts[1]);
    if (!Number.isFinite(month) || !Number.isFinite(day)) return new Date(NaN);
    return zonedDate(year, month, day, customizationEndTimePT);
  };

  /** @param {string} selector @param {(elements: NodeListOf<Element>) => void} callback @param {number} [delay=1] @param {number} [failsafeDelay] */
  const waitForElement = (selector, callback, delay = 1, failsafeDelay) => {
    const run = () => {
      const elements = document.querySelectorAll(selector);
      if (!elements.length) return false;
      if (typeof callback === "function") {
        setTimeout(() => callback(document.querySelectorAll(selector)), delay);
      }
      return true;
    };

    if (run()) return;

    const observeRoot = document.body || document.documentElement;
    const observer = new MutationObserver((_mutations, obs) => {
      if (run()) obs.disconnect();
    });

    observer.observe(observeRoot, {
      childList: true,
      subtree: true
    });

    if (failsafeDelay) setTimeout(() => observer.disconnect(), failsafeDelay);
  };

  // Like waitForElement, but waits for the element to be present AND actually
  // clickable. The app renders the line-edit button disabled until its bundle data
  // loads (ProductOptions.tsx:257 -> buttonDisabled={!bundleIsReady}) and the shared
  // Button sets BOTH the `disabled` attribute and a `disabled` class
  // (components/Button/index.tsx:57-59). The browser drops clicks on a disabled
  // <button>, so firing on mere existence is a silent no-op — which is exactly the
  // bug this fixes.
  //
  // Two details that matter:
  //  - observe ATTRIBUTES, not just childList: React flips `disabled` in place, so
  //    a childList-only observer never wakes up.
  //  - re-query every check instead of holding a reference: React may swap the node
  //    on re-render, leaving a detached element that can never be clicked.
  /**
   * @param {string} selector
   * @param {(el: any) => void} callback
   * @param {number} [timeout]
   */
  const waitForEnabledElement = (selector, callback, timeout = 20000) => {
    let settled = false;
    const isInteractive = (el) =>
      !!el && !el.disabled && !el.classList.contains("disabled") && el.getAttribute("aria-disabled") !== "true";

    const check = () => {
      if (settled) return;
      const el = document.querySelector(selector);
      if (!isInteractive(el)) return;
      settled = true;
      cleanup();
      callback(el);
    };

    const observer = new MutationObserver(check);
    // Polling backs up the observer for anything it can't see (e.g. a property set
    // without a corresponding attribute mutation).
    const poll = setInterval(check, 150);
    const failsafe = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      awtLog("waitForEnabledElement: timed out waiting for an enabled", selector);
    }, timeout);

    function cleanup() {
      observer.disconnect();
      clearInterval(poll);
      clearTimeout(failsafe);
    }

    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "class", "aria-disabled"]
    });
    check();
  };

  //awt cp
  let cpLoaded = false;
  awtLog("script loaded", { pathname: location.pathname, search: location.search });
  if (!window.location.search.includes("subscriptionId=")) {
    awtLog("CP block SKIPPED — no subscriptionId= in the query string");
  }
  if (window.location.search.includes("subscriptionId=")) {
    document.body && document.body.classList.add("awt-cp", "cp-edit-hidden");

    // Debugger hook — fires when the line-edit button is clicked. Real
    // functionality TBD.
    document.addEventListener(
      "click",
      (event) => {
        const target = /** @type {Element} */ (event.target);
        const editBtn = target && target.closest && target.closest("button.awtomatic-line-edit-button");
        if (editBtn) {
          // Click the first action ("link") of a list. Use a full
          // mousedown -> mouseup -> click sequence: a bare .click() can be
          // ignored (e.g. Chakra records the mousedown target and only acts if
          // mouseup/click lands on that same element).
          /** @param {Element} el */
          const simulateClick = (el) => {
            ["mousedown", "mouseup", "click"].forEach((type) => {
              el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
            });
          };
          // The first action is either the first <a> in a desktop inline action
          // list, or the first button/link inside the first <li> of the mobile
          // bottom-sheet modal (e.g. "Change selection").
          /** @param {Element | null} root */
          const clickFirstAction = (root) => {
            if (!root) return;
            const first = root.querySelector(".bundle-action-list a") || root.querySelector("li:first-child a, li:first-child button");
            if (first) simulateClick(first);
          };

          // Case 1 — desktop inline action list next to the edit button.
          clickFirstAction(editBtn.closest(".bundle-action-list-container"));

          // Case 2 — mobile bottom-sheet modal opens asynchronously; wait for it,
          // then click its first action.
          waitForElement(
            ".subscription-overlay-open .chakra-modal__content-container",
            () => {
              clickFirstAction(document.querySelector(".subscription-overlay-open .chakra-modal__content-container"));
            },
            100,
            10000
          );
        }
      },
      true
    );

    const runCpEditGate = async () => {
      if (cpLoaded) return; // fire once per detail-view load
      cpLoaded = true;
      awtLog("CP gate: running");

      //check sub lines and add brand classes to body for cp
      try {
        const subscriptionDetails = awtomicWindow.Awtomic?.subscriptionDetails;
        const lines = subscriptionDetails?.Lines;
        if (!lines || !document.body) {
          awtLog("CP gate: bailed — no Awtomic.subscriptionDetails.Lines yet", { hasDetails: !!subscriptionDetails });
          cpLoaded = false; // allow a later attempt to run
          return;
        }

        // PICKUP subscriptions are non-editable when disablePickupEdit is on.
        const pickupBlocked = disablePickupEdit && isPickupMember();

        // check releaseWindowData for matched bundle id and only remove cp-edit-hidden when every matched line is inside its window

        let matchedReleaseWindowLineCount = 0;
        let matchedReleaseWindowLineCountInsideWindow = 0;
        // CX-4775 — set true if any line is an "ignore"/exception product
        let hasEditBlockException = false;
        const multiBundle = hasMultipleReleaseWindowBundles(lines);

        for (let i = 0; i < lines.length; i++) {
          // Match the subs-list gate: a box line carries the bundle's id in
          // _bundleProductId, and its own ProductId is the container variant's
          // product, which is not in releaseWindowData.
          const productId = getLineAttr(lines[i], "_bundleProductId") || (lines[i] && lines[i].ProductId);
          if (!productId) continue;

          // CX-4775 — exception products bypass the cp-edit block entirely
          if (isEditBlockExceptionProductId(productId)) hasEditBlockException = true;

          // customization-window gating (skipped when useCustomizationWindow is false)
          if (useCustomizationWindow) {
            const data = getReleaseWindowFromProductId(productId);
            if (data) {
              matchedReleaseWindowLineCount += 1;
              const now = new Date();
              const currentYear = now.getFullYear();
              const windowDates = releaseWindowDatesFor(data, multiBundle);
              const customizationStarts = parseMonthDayStart(windowDates.customizationStarts, currentYear);
              const customizationEnds = parseMonthDayEnd(windowDates.customizationEnds, currentYear);

              if (customizationEnds < customizationStarts) {
                customizationEnds.setFullYear(customizationEnds.getFullYear() + 1);
              }

              if (now >= customizationStarts && now <= customizationEnds) {
                matchedReleaseWindowLineCountInsideWindow += 1;
              }
            }
          }
        }

        awtLog("CP gate", {
          pickupBlocked,
          multiBundle,
          hasEditBlockException,
          matchedReleaseWindowLineCount,
          matchedReleaseWindowLineCountInsideWindow
        });

        // CX-5123 — the 30-day "recently billed" rule is gone. HALL asked for it
        // to be dropped in favour of the hold flag, which the subs-list gate
        // applies via subscriptionAllowsBoxEdit; the CP itself already hides its
        // own edit box for a held subscription.
        if (
          !disableCustomization && // CX-5056 failsafe global
          !pickupBlocked && // PICKUP subs stay non-editable regardless of other gates
          (hasEditBlockException || // CX-4775 — exception products bypass the release-window gate
            !matchedReleaseWindowLineCount ||
            matchedReleaseWindowLineCount === matchedReleaseWindowLineCountInsideWindow)
        ) {
          awtLog("CP gate -> edit SHOWN");
          document.body.classList.remove("cp-edit-hidden");
        } else {
          awtLog("CP gate -> edit HIDDEN");
        }
      } catch (e) {
        awtLog("CP gate: threw", e);
        // silent fail
      }

      // Auto-open the box editor when arriving from the subs-list Customize button.
      const editingBundleId = localStorage.getItem("editingBundleId");
      if (editingBundleId) {
        // Clear the flag immediately so a refresh doesn't re-trigger it.
        localStorage.removeItem("editingBundleId");

        const rowSelector = `.bundle-table__body-row.pv_${CSS.escape(editingBundleId)}`;

        // The app tells us when the box editor is really open, so we never have to
        // guess (BundleUpdate/index.tsx:115-122). Use that to stop retrying — and,
        // importantly, to avoid a second click that would toggle it back shut.
        let boxEditorOpen = document.body.classList.contains("awt-bab-edit-open");
        const markOpen = () => {
          boxEditorOpen = true;
        };
        window.addEventListener("awtomic-box-edit-modal-opened", markOpen);
        // Stop listening once the flow is over either way.
        setTimeout(() => window.removeEventListener("awtomic-box-edit-modal-opened", markOpen), 30000);
        const isBoxEditorOpen = () => boxEditorOpen || document.body.classList.contains("awt-bab-edit-open");

        // Wait for the row's edit button to become ENABLED. It renders disabled
        // ("bundle-button ... awtomatic-line-edit-button button--secondary disabled")
        // while the bundle loads; a click before that is dropped by the browser.
        waitForEnabledElement(`${rowSelector} .awtomatic-line-edit-button`, (btn) => {
          awtLog("auto-edit: line-edit button enabled, clicking", editingBundleId);
          btn.click();

          // Clicking it also trips the document-level handler above, which opens the
          // first action (edit_my_box) on desktop and in the mobile sheet. Rather
          // than blind-clicking the action again — which would toggle it closed —
          // verify, and only act if we didn't get there.
          let attempts = 0;
          const ensureBoxEditorOpen = () => {
            if (isBoxEditorOpen()) {
              awtLog("auto-edit: box editor open");
              return;
            }
            if (++attempts > 3) {
              awtLog("auto-edit: gave up opening the box editor after", attempts - 1, "retries");
              return;
            }
            const editBoxBtn = document.querySelector(`${rowSelector} .bundle-action-item--edit_my_box`);
            if (editBoxBtn && typeof editBoxBtn.click === "function") {
              awtLog("auto-edit: box editor not open, clicking 'edit my box' directly (attempt", attempts, ")");
              editBoxBtn.click();
            } else {
              awtLog("auto-edit: no 'edit my box' action found yet (attempt", attempts, ")");
            }
            setTimeout(ensureBoxEditorOpen, 900);
          };
          setTimeout(ensureBoxEditorOpen, 900);
        });
      }

      // Wrap the Cancel + Pause buttons inside a "Membership options" accordion.
      // Awtomic re-renders the button area on edits, so this is idempotent and
      // re-runs from a MutationObserver: if the live Cancel button isn't already
      // inside our accordion, rebuild it (dropping any stale accordion).
      const buildMembershipAccordion = () => {
        // only build when Cancel exists; if it does, include the Pause button
        // too (even though it's hidden via CSS). So: cancel+pause, cancel, or nothing.
        const cancelBtn = document.querySelector("body.awt-cp .bundle-button--cancel");
        if (!cancelBtn) return; // no Cancel -> nothing to wrap
        if (cancelBtn.closest(".awt-membership-options")) return; // already wrapped
        // drop any stale accordion left over from a previous render
        document.querySelectorAll(".awt-membership-options").forEach((el) => el.remove());

        const pauseBtn = document.querySelector("body.awt-cp .bundle-button--pause");

        const acc = document.createElement("div");
        acc.className = "awt-membership-options";

        const header = document.createElement("button");
        header.type = "button";
        header.className = "awt-membership-options__header";
        header.setAttribute("aria-expanded", "false");
        header.innerHTML =
          '<span class="awt-membership-options__icon" aria-hidden="true"></span>' + '<span class="awt-membership-options__label">Membership options</span>';

        const panel = document.createElement("div");
        panel.className = "awt-membership-options__panel";
        const panelInner = document.createElement("div");
        panelInner.className = "awt-membership-options__panel-inner";
        panel.appendChild(panelInner);

        header.addEventListener("click", () => {
          const open = acc.classList.toggle("is-open");
          header.setAttribute("aria-expanded", open ? "true" : "false");
        });

        // place the accordion where the Cancel button was, then move the buttons
        // into the panel (moving preserves Awtomic's click handlers)
        cancelBtn.parentNode.insertBefore(acc, cancelBtn);
        acc.appendChild(header);
        acc.appendChild(panel);
        if (pauseBtn) panelInner.appendChild(pauseBtn);
        panelInner.appendChild(cancelBtn);
      };

      waitForElement(".bundle-button--cancel", buildMembershipAccordion, 100, 15000);
      const membershipAccObserver = new MutationObserver(buildMembershipAccordion);
      membershipAccObserver.observe(document.body, { childList: true, subtree: true });
    };

    // This file is loaded with `async`, so on a fast CP render Awtomic can
    // dispatch awtomic-cp-loaded BEFORE this listener is attached — a one-shot
    // event listener then misses it forever and the gate never runs (no logs at
    // all on the CP). Listen for the event AND poll for the data the event
    // carries, so whichever happens first wins. runCpEditGate is idempotent.
    window.addEventListener("awtomic-cp-loaded", runCpEditGate);
    awtLog("CP block armed — listening for awtomic-cp-loaded", {
      alreadyHasDetails: !!awtomicWindow.Awtomic?.subscriptionDetails
    });
    let cpPollAttempts = 0;
    const cpPoll = setInterval(() => {
      cpPollAttempts += 1;
      if (cpLoaded || cpPollAttempts > 60) {
        // 60 * 500ms = 30s
        clearInterval(cpPoll);
        if (!cpLoaded) awtLog("CP gate: gave up waiting for Awtomic.subscriptionDetails after 30s");
        return;
      }
      if (awtomicWindow.Awtomic?.subscriptionDetails?.Lines) {
        awtLog("CP gate: picked up subscriptionDetails via poll (event was missed or fired early)");
        clearInterval(cpPoll);
        runCpEditGate();
      }
    }, 500);


    // CX-4727 item 3a — treat a just-triggered order / just-finished edit as
    // blocked: re-add cp-edit-hidden immediately so the UI assumes editing is
    // off until awtomic-cp-loaded re-asserts the real window state. UI
    // deterrent only — there is no server-side order-now lockout.
    const cx4727Reblock = () => {
      document.body && document.body.classList.add("cp-edit-hidden");
    };
    window.addEventListener("awtomic-force-order-completed", cx4727Reblock);
    // window.addEventListener("awtomic-box-edit-modal-closed", cx4727Reblock);

    // CX-4727 item 4 — persistent confirmation banner after a forced order.
    // The built-in success toast closes instantly on HALL, so inject a
    // persistent .awt-info-style banner instead. Dismisses on navigation.
    window.addEventListener("awtomic-force-order-completed", () => {
      try {
        if (document.querySelector(".awt-order-confirmation-banner")) return; // idempotent
        const banner = document.createElement("div");
        banner.className = "awt-info awt-order-confirmation-banner";
        banner.innerHTML = '<p style="">Your order has been triggered successfully.</p>';
        waitForElement(
          ".bundle-details-table-title",
          (elements) => {
            if (document.querySelector(".awt-order-confirmation-banner")) return;
            const anchor = elements && elements[0];
            if (anchor && anchor.parentNode) {
              anchor.parentNode.insertBefore(banner, anchor);
            } else {
              document.body && document.body.appendChild(banner);
            }
          },
          100,
          10000
        );
      } catch (_e) {
        // silent fail — never break the CP
      }
    });

    // After a forced order completes on the subscription detail view, send the
    // customer to the subscription list (the same portal path without
    // ?subscriptionId=) with an order-placed flag so the confirmation banner
    // shows on the list. Only fires from a detail view, so it never affects
    // normal navigation.
    window.addEventListener("awtomic-force-order-completed", () => {
      try {
        if (!window.location.search.includes("subscriptionId=")) return;
        const url = new URL(window.location.href);
        url.searchParams.delete("subscriptionId");
        url.searchParams.set("awt-order-placed", "1");
        // small delay so the customer perceives the success state first
        setTimeout(() => {
          window.location.assign(url.pathname + url.search + url.hash);
        }, 1200);
      } catch (_e) {
        // silent fail — never break the CP
      }
    });

    // CX-4960 — states we cannot ship to for compliance reasons. Same bare
    // two-letter text test as the " CA " check below.
    // KNOWN RISK, stated on the ticket: a bare two-letter test can match text
    // that is not a state — " DE " and " ME " are the dangerous ones, since a
    // street or city line can contain them (as can a product name). Narrowing
    // this means reading the state out of the address element only.
    const cx4960NoShipStates = ["AL", "AR", "DE", "KY", "LA", "ME", "MS", "NJ", "RI", "UT"];
    const cx4960StateRe = new RegExp(`\\s(${cx4960NoShipStates.join("|")})\\s`);

    //wait for .bundle-shipment-body and debugger
    waitForElement(
      ".bundle-shipment-body",
      (element) => {
        console.log(element);
        const infoDiv = document.createElement("div");
        infoDiv.className = "awt-info";

        // General disclaimer — shown to everyone, always first regardless of state.
        infoDiv.innerHTML = `<p style="">Ground shipping may be unavailable or significantly delayed due to extreme hot or cold temperatures.</p>`;

        //element is a node list check if includes " CA " if so add the CA-only disclaimers
        if (Array.from(element).some((el) => el.textContent && el.textContent.includes(" CA "))) {
          // CX-4727 item 2 — CRV above the two WARNING paragraphs (reordered).
          infoDiv.innerHTML += `<p style="">A California CRV fee of $0.05 per 375mL bottle and $0.10 per 750mL and 1.5L bottles will be applied to all CA orders.</p>
        <p style="">WARNING: Many food and beverage cans have linings containing bisphenol A (BPA), a chemical known to the State of California to cause harm to the female reproductive system. Jar lids and bottle caps may also contain BPA. You can be exposed to BPA when you consume foods or beverages packaged in these containers. For more information go to: <a href="http://www.p65warnings.ca.gov/bpa" target="_blank" rel="noopener noreferrer">www.p65warnings.ca.gov/bpa</a></p>
        <p style="">WARNING: Drinking distilled spirits, beer, coolers, wine and other alcoholic beverages may increase cancer risk, and, during pregnancy, can cause birth defects. For more information go to: <a href="http://www.p65warnings.ca.gov/alcohol" target="_blank" rel="noopener noreferrer">www.p65warnings.ca.gov/alcohol</a></p>`;
        }

        // CX-4960 — no-ship compliance warning, same container and same place
        // in the flow as the California copy above (CA is not on the list, so
        // the two never both apply).
        if (Array.from(element).some((el) => el.textContent && cx4960StateRe.test(el.textContent))) {
          infoDiv.innerHTML += `<p class="awt-compliance-states">The product in your cart is not available for delivery to your location, please update your delivery address.</p>
        <p class="awt-compliance-states">Due to compliance laws, we are unable to ship to the following states: ${cx4960NoShipStates.join(", ")}</p>`;
        }

        // track the last state we acted on, so multiple DOM mutations that land
        // on the same boundary only fire once per real transition
        const placeAfterTotals = () => {
          const totals = document.querySelector(".awt-confirmation-totals");
          if (!totals) return false;
          // insert the disclaimer info right after the totals as a sibling
          totals.parentNode.insertBefore(infoDiv, totals.nextSibling);
          return true;
        };
        let wasPresent = false;
        const observer = new MutationObserver(() => {
          const isPresent = !!document.querySelector(".awt-confirmation-totals");
          if (isPresent === wasPresent) return; // no real change since last time
          wasPresent = isPresent;
          if (isPresent) placeAfterTotals();
        });
        observer.observe(document.body, { childList: true, subtree: true });

        // handle the case where it is already present on load
        if (placeAfterTotals()) wasPresent = true;
      },
      100,
      10000
    );

    // CX-4726 item 10 — swap "Edit" -> "Customize" on the wine clubs.
    // CSS content: can't reliably replace a real text node across CP
    // re-renders, so do it in JS via waitForElement (re-fires on mutation).
    const cx4726SwapEditLabel = (elements) => {
      elements.forEach((el) => {
        // Replace only the exact "Edit" text, preserving any icon nodes.
        el.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim() === "Edit") {
            node.textContent = node.textContent.replace("Edit", "Customize");
          }
        });
        // Fallback: if the button's whole label is "Edit" with no child icon.
        if (el.children.length === 0 && el.textContent && el.textContent.trim() === "Edit") {
          el.textContent = "Customize";
        }
      });
    };
    waitForElement(".awtomatic-line-edit-button", cx4726SwapEditLabel, 100, 15000);
  }

  // ===================================================================
  // CX-4726 — [HALL] General CP Edits (rev 3)
  // Per-brand membership copy for item 3. Keyed by body[data-brand]
  // value (hall | walt | baca | michelfoch). Each brand has one or more
  // membership tiers (title + paragraphs). Source: HALL CX attachment
  // custom-copy-under-subscription-ilst.txt.
  // ===================================================================
  /** @type {Record<string, { heading: string, tiers: { title: string, paragraphs: string[] }[] }>} */
  const cx4726MembershipCopy = {
    hall: {
      heading: "Membership Information",
      tiers: [
        {
          title: "The Collection",
          paragraphs: [
            "The Collection offers access to some of HALL's most limited and highly sought-after Cabernet Sauvignon releases. Many of our smallest production wines and single-vineyard bottlings are reserved exclusively for Collection Members.",
            "Typical club shipments include two of our newest releases, with the option to receive 2, 4, or 6 bottles per shipment. If you choose 6 bottle shipments you will enjoy $5 standard shipping on all club shipments.",
            "As our most elevated membership, The Collection offers deeper access to HALL's cellar along with enhanced benefits — including invitations to Collection Member–exclusive events, rare library releases, additional complimentary tastings, and more throughout the year."
          ]
        },
        {
          title: "Primary Colors",
          paragraphs: [
            "Primary Colors membership offers access to many of our most beloved wines, including Cabernet Sauvignon and other Bordeaux varietals available directly from the winery.",
            "Typical club shipments include two of our newest releases, with the option to receive 2, 4, or 6 bottles per shipment. If you choose 6 bottle shipments you will enjoy $5 standard shipping on all club shipments.",
            "Primary Colors is a great way to stay connected to HALL, with flexibility and exclusive member access throughout the year.",
            "Ready for more? Primary Colors members can upgrade to The Collection at any time to unlock our full suite of enhanced benefits and experiences."
          ]
        }
      ]
    },
    walt: {
      heading: "Membership Information",
      tiers: [
        {
          title: "Vintner's Club",
          paragraphs: [
            "Membership in the Vintner's Club grants you access to our most precious single vineyard wines sourced from premier Pacific Coast vineyards, each with its unique expression of the land. Winemaker, Megan Gunderson Paredes, selects two wines to be featured in each quarterly shipment. As a member, you may elect to receive one bottle of each wine (a two bottle shipment), or increase your guaranteed allocation by claiming two or three of each selection (a four or six bottle shipment respectively). The average price for a two bottle shipment is $135 + shipping and tax. Shipments of six bottles or more enjoy $5 standard shipping."
          ]
        },
        {
          title: "Appellation Membership",
          paragraphs: [
            "Our Appellation Membership grants priority access to all our WALT Collection wines after Vintner's Club access. Shipments include wines from the WALT Appellation and our Single Vineyard Portfolio. The average price per shipment is $100 + shipping and tax. Shipments of six bottles or more enjoy $5 standard shipping."
          ]
        }
      ]
    },
    baca: {
      heading: "Membership Information",
      tiers: [
        {
          title: "Be BACA.",
          paragraphs: [
            "Like you, we're seekers of adventure, spectacular wine, and unforgettable experiences. We bring a fresh approach to winemaking but our origin story includes some of California's best winemakers and vintners. Best of both worlds, we say.",
            "Our club is called THE LIST",
            "BACA is available in very limited quantities. But as a member of THE LIST you'll have first access to our releases. The average price per shipment is $100 + shipping and tax. Shipments of six bottles or more enjoy $5 standard shipping. Join now, joy later."
          ]
        }
      ]
    },
    michelfoch: {
      heading: "Membership Information",
      tiers: [
        {
          title: "Michel Foch Membership",
          paragraphs: [
            "Membership with Michel Foch offers French Champagne directly to your door four times per year. Receive access to our member-only wines and discounts across the entire portfolio including wines from Michel Foch, HALL, WALT, and BACA.",
            "Typically, each club shipment will include two of our latest releases. Choose to receive a total of 2, 4 or 6 bottles in each shipment. If you choose 6 bottle shipments, you will enjoy $5 standard shipping on all club shipments.",
            "Perfect for those looking for the ultimate in access and flexibility! We will always email you ahead of each shipment, notifying you of what exciting new releases are reserved for you. At that time, you are welcome to contact us and customize your shipment."
          ]
        }
      ]
    }
  };

  /** Signup + per-brand benefits page paths (built in CX-4671).
   * Signup is a single shared page (template page.club-signup.json); the
   * benefits page is per-brand (page.{hall|walt|baca|mf}-membership-benefits
   * .json all exist in this theme). Page HANDLES are assumed to match the
   * template stems — confirm in the open questions if HALL renamed any. */
  const CX4726_SIGNUP_PATH = "/pages/club-signup";
  /** @type {Record<string, { signup: string, benefits: string }>} */
  const cx4726BrandPages = {
    hall: { signup: CX4726_SIGNUP_PATH, benefits: "/pages/hall-membership-benefits" },
    walt: { signup: CX4726_SIGNUP_PATH, benefits: "/pages/walt-membership-benefits" },
    baca: { signup: CX4726_SIGNUP_PATH, benefits: "/pages/baca-membership-benefits" },
    michelfoch: { signup: CX4726_SIGNUP_PATH, benefits: "/pages/mf-membership-benefits" }
  };

  /** @returns {string} the current brand key from body[data-brand], or "hall" as a safe default */
  const cx4726GetBrand = () => {
    const b = document.body && document.body.getAttribute("data-brand");
    return b && cx4726MembershipCopy[b] ? b : "hall";
  };

  // When arriving on the subscription list right after a forced order
  // (order-placed flag present in the URL), render the confirmation banner on
  // the list, anchored to the list container.
  try {
    if (window.location.search.includes("awt-order-placed=1")) {
      const banner = document.createElement("div");
      banner.className = "awt-info awt-order-confirmation-banner";
      banner.innerHTML = '<p style="">Your order has been triggered successfully.</p>';
      waitForElement(
        ".bundleapp-subscription-list-item, .bundleapp-subscription-list, .bundle-subscriptions-list-container",
        (elements) => {
          if (document.querySelector(".awt-order-confirmation-banner")) return;
          const anchor = elements && elements[0];
          if (anchor && anchor.parentNode) {
            anchor.parentNode.insertBefore(banner, anchor);
          } else {
            document.body && document.body.appendChild(banner);
          }
        },
        100,
        10000
      );
    }
  } catch (_e) {
    // silent fail — never break the CP
  }

  //my subscruptions list page
  // CX-4726 rev 4 — the Awtomic CP renders client-side wherever the app
  // mounts; on HALL it is NOT served at the literal /tools/bundle-subscriptions
  // path, so the old exact-URL gate never matched and body.awt-my-subs was
  // never applied (Brian, comment 67762). Detect the list by DOM presence
  // instead, and exclude the detail view (?subscriptionId=) + the BaB flow so
  // the list-only logic doesn't fire there. Add awt-my-subs the moment the
  // subscription list appears (it can mount after first paint).
  const cx4726IsDetailView = window.location.search.includes("subscriptionId=");
  const cx4726IsBabFlow = window.location.pathname.includes("/tools/bundle-subscriptions/bundle/");
  if (!cx4726IsDetailView && !cx4726IsBabFlow) {
    // CX-4726 items 1, 1a, 2, 3 — render below the subscriptions list.
    // The list item itself is the reliable, app-rendered hook (works on any
    // page/handle the merchant mounts the CP under).
    waitForElement(
      ".bundleapp-subscription-list-item, .bundleapp-subscription-list, .bundle-subscriptions-list-container",
      () => {
        try {
          // Apply the body class here (not behind a URL gate) so the
          // body.awt-my-subs CSS (items 4/5) reliably scopes to the real list.
          document.body && document.body.classList.add("awt-my-subs");

          if (document.querySelector(".cx4726-cp-footer")) return; // idempotent

          const brand = cx4726GetBrand();
          const pages = cx4726BrandPages[brand];
          const copy = cx4726MembershipCopy[brand];

          // Determine whether the customer has any ACTIVE subscriptions.
          const listItems = document.querySelectorAll(".bundleapp-subscription-list-item");
          let hasActiveSub = false;
          listItems.forEach((item) => {
            const cancelled =
              item.querySelector(".bundleapp-subscription-list-item__subscription-not-active-status") ||
              item.classList.contains("bundleapp-subscription-list-item--cancelled");
            if (!cancelled) hasActiveSub = true;
          });

          const footer = document.createElement("div");
          footer.className = "cx4726-cp-footer";

          // Wrapper for buttons to display side by side.
          const buttonsContainer = document.createElement("div");
          buttonsContainer.className = "cx4726-buttons-container";

          // Item 1 / 1a — contextual Join / Add button.
          const joinBtn = document.createElement("a");
          joinBtn.className = "cx4726-membership-cta membership-btn";
          joinBtn.href = pages.signup;
          joinBtn.textContent = hasActiveSub ? "Add a wine club membership" : "Join a wine club membership";
          buttonsContainer.appendChild(joinBtn);

          // Item 2 — "Your membership benefits" button.
          const benefitsBtn = document.createElement("a");
          benefitsBtn.className = "cx4726-benefits-cta membership-btn";
          benefitsBtn.href = pages.benefits;
          benefitsBtn.textContent = "Your membership benefits";
          buttonsContainer.appendChild(benefitsBtn);

          footer.appendChild(buttonsContainer);

          // Item 3 — brand-contextual membership info block.
          if (copy) {
            const section = document.createElement("div");
            section.className = "membership-section cx4726-membership-info";

            const container = document.createElement("div");
            container.className = "membership-container";

            const title = document.createElement("h2");
            title.className = "membership-title";
            title.textContent = copy.heading;
            container.appendChild(title);

            copy.tiers.forEach((tier) => {
              const tierTitle = document.createElement("h3");
              tierTitle.className = "column-title";
              tierTitle.textContent = tier.title;
              container.appendChild(tierTitle);

              tier.paragraphs.forEach((p) => {
                const para = document.createElement("p");
                para.className = "membership-description";
                para.textContent = p;
                container.appendChild(para);
              });
            });

            section.appendChild(container);
            footer.appendChild(section);
          }

          // Place the footer at the bottom of the list. Prefer inserting
          // right after the actual list container so it reads as part of the
          // list; fall back to body so it always renders somewhere visible.
          const listContainer =
            document.querySelector(".bundleapp-subscription-list") ||
            document.querySelector(".bundle-subscriptions-list-container") ||
            (document.querySelector(".bundleapp-subscription-list-item") && document.querySelector(".bundleapp-subscription-list-item").parentElement);
          if (listContainer && listContainer.parentNode) {
            listContainer.parentNode.insertBefore(footer, listContainer.nextSibling);
          } else {
            document.body.appendChild(footer);
          }
        } catch (_e) {
          // silent fail — never break the list page
        }
      },
      100,
      10000
    );
  }

  //bab purchase  flow
  if (window.location.pathname.includes("/tools/bundle-subscriptions/bundle/")) {
    awtomicWindow.Awtomic = awtomicWindow.Awtomic || {};
    awtomicWindow.Awtomic.getBabSelection = () => {
      const babProductId = document
        .querySelector(".bundle-container")
        ?.className.split(" ")
        .find((c) => c.startsWith("pv_"))
        ?.split("_")[1];
      if (!babProductId) return { object: {}, queryString: "" };

      const bundleProductCards = document.querySelectorAll(".bundle-product-card");
      /** @type {Record<string, number>} */
      const bundleSelectedProducts = {};

      bundleProductCards.forEach((card) => {
        const productId = card.getAttribute("data-pid");
        const quantityElement = card.querySelector(".bundle-product-card__quantity-button-container > span");
        const quantity = quantityElement ? parseInt(quantityElement.innerHTML, 10) : 0;
        if (productId && quantity > 0) {
          bundleSelectedProducts[productId] = quantity;
        }
      });

      const queryString = Object.entries(bundleSelectedProducts)
        .map(([id, quantity]) => `${id}=${quantity}`)
        .join("&");

      const currentUrl = window.location.href;
      const separator = currentUrl.includes("?") ? "&" : "?";
      const fullUrl = queryString ? `${currentUrl}${separator}${queryString}` : currentUrl;

      return {
        object: { [babProductId]: bundleSelectedProducts },
        queryString,
        fullUrl
      };
    };

    const urlParts = window.location.pathname.split("/");
    const bundleId = urlParts[urlParts.length - 1];

    if (bundleId) {
      document.body && document.body.classList.add(`awt-bundle-${bundleId}`);
    }
  }

  //"My Clubs" / my subs
  // Match the proxy path anywhere in the URL (a locale prefix like /fr/ or a
  // trailing segment shouldn't matter), but stay off the single-subscription CP
  // (?subscriptionId=) and the BaB flow, which are different views. scanList also
  // requires the list container, so nothing fires on a page without a list.
  if (location.pathname.includes("tools/bundle-subscriptions") && !cx4726IsDetailView && !cx4726IsBabFlow) {
    // The list streams in: the container mounts first, then items, and an item's
    // Manage button (inside __r2c3) can land after the item element itself. The old
    // code fired once on "a list item exists" and did a single querySelectorAll over
    // BUTTONS — so any item whose button hadn't mounted yet was skipped and never
    // revisited, which is why the Customize button appeared only sometimes.
    //
    // Instead: wait for the real container, iterate over ITEMS (so an item with no
    // button yet is simply left for a later pass), and keep scanning on mutations.
    const listContainerSelector = ".bundleapp-subscriptions-list__container";
    const listItemSelector = ".bundleapp-subscription-list-item[data-subscription-id]";
    // Our injected button copies the Manage button's classes, so it would match the
    // Manage selector on a re-scan and get cloned again — exclude it by its marker.
    const customizeButtonSelector = "[data-bundle-variant-id]";
    const manageButtonSelector = `button.bundle-button.button--primary:not(${customizeButtonSelector})`;

    // Items we've taken responsibility for. Keyed on the element, so if the app
    // re-mounts a row (new node) it is evaluated again from scratch.
    const claimedListItems = new WeakSet();
    let loggedShopSettings = false;

    const scanList = () => {
      // Honour the full structure: don't touch anything until the container exists.
      if (!document.querySelector(listContainerSelector)) return;

      if (!loggedShopSettings) {
        loggedShopSettings = true;
        const cps = getCustomerPortalSettings();
        awtLog("list gate — shop settings (window.reactInit)", {
          reactInitPresent: !!awtomicWindow.reactInit,
          customerPortalSettingsPresent: !!cps,
          productChange: cps?.productChange ?? "(unreadable)",
          productChangeBlocks: productChangeNotAllowed()
        });
      }

      document.querySelectorAll(listItemSelector).forEach(async (listItem) => {
        // Leave the item alone until its Manage button exists — a later mutation
        // will bring us back. This is the case the old one-shot pass dropped.
        const button = listItem.querySelector(manageButtonSelector);
        if (button) {
          if (claimedListItems.has(listItem)) return;
          if (listItem.querySelector(customizeButtonSelector)) return; // already done
          const subscriptionId = listItem.getAttribute("data-subscription-id");
          const firstChildDiv = listItem.querySelector(":scope > div:first-child");
          if (firstChildDiv && subscriptionId) {
            // Claim before any await so a second scan can't double-process it.
            claimedListItems.add(listItem);
            // Find subscription data
            const subscription = window.Awtomic?.subscriptionList?.find((sub) => sub.SubscriptionId?.endsWith(subscriptionId));
            if (!subscription) {
              awtLog("list gate", subscriptionId, "-> no subscription in Awtomic.subscriptionList");
              return;
            }

            // Check if this subscription has a bundle line (has _bundleUid)
            const hasBundle = subscription.Lines?.some((line) => line.CustomAttributes?.some((attr) => attr.key === "_bundleUid"));
            if (!hasBundle) {
              awtLog("list gate", subscriptionId, "-> not a bundle subscription");
              return; // Skip if not a bundle subscription
            }

            // Subscription-level half of the app's render gate — status, the
            // billing-vs-delivery interval mismatch the app calls "prepaid", and
            // in-flight billing/updating. All present in the list payload.
            const subGate = subscriptionAllowsBoxEdit(subscription);
            if (!subGate.ok) {
              awtLog("list gate", subscriptionId, `-> subscription not editable: ${subGate.reason}`);
              return;
            }

            // Only offer Customize on a line the app will actually let the CP
            // edit — otherwise the button hands the customer to a detail view
            // with no "Edit my box" action and nothing happens. Covers the
            // admin block (_awtMods), classic/shuffle boxes, addons, gift and
            // free lines. See lineOffersEditMyBox above.
            const bundleLine = subscription.Lines?.filter((line) => line.CustomAttributes?.some((attr) => attr.key === "_bundleUid")).find(
              lineOffersEditMyBox
            );
            if (!bundleLine) {
              const firstBundleLine = subscription.Lines?.find((line) => line.CustomAttributes?.some((attr) => attr.key === "_bundleUid"));
              awtLog("list gate", subscriptionId, "-> no editable box line (app would not offer 'Edit my box')", {
                _awtMods: getLineAttr(firstBundleLine, "_awtMods") ?? "(none)",
                _bundleType: getLineAttr(firstBundleLine, "_bundleType") ?? "(none)",
                _bundleProductId: getLineAttr(firstBundleLine, "_bundleProductId") ?? "(none)",
                _addon: getLineAttr(firstBundleLine, "_addon") ?? "(none)",
                gift: isGiftLine(firstBundleLine),
                free: isFreeLine(firstBundleLine)
              });
              return;
            }

            // Get the _variantId from that line
            const variantId = bundleLine.CustomAttributes?.find((attr) => attr.key === "_variantId")?.value;
            if (!variantId) {
              awtLog("list gate", subscriptionId, "-> bundle line has no _variantId");
              return;
            }

            // Check if editing is allowed based on release window and billing history
            let isEditingAllowed = !disableCustomization; // CX-5056 failsafe global
            let hasEditBlockException = false;

            // Check for exception products (CX-4775)
            // For bundles, use the _bundleProductId from CustomAttributes, not the line's ProductId
            if (subscription.Lines) {
              for (const line of subscription.Lines) {
                const bundleProductId = line.CustomAttributes?.find((attr) => attr.key === "_bundleProductId")?.value;
                const productIdToCheck = bundleProductId || line.ProductId;

                if (productIdToCheck && isEditBlockExceptionProductId(productIdToCheck)) {
                  hasEditBlockException = true;
                  break;
                }
              }
            }

            if (!hasEditBlockException) {
              // Check release window
              let matchedReleaseWindowLineCount = 0;
              let matchedReleaseWindowLineCountInsideWindow = 0;

              // customization-window gating (skipped when useCustomizationWindow is false)
              if (useCustomizationWindow && subscription.Lines) {
                const multiBundle = hasMultipleReleaseWindowBundles(subscription.Lines);
                for (const line of subscription.Lines) {
                  // For bundles, use the _bundleProductId from CustomAttributes
                  const bundleProductId = line.CustomAttributes?.find((attr) => attr.key === "_bundleProductId")?.value;
                  const productId = bundleProductId || line.ProductId;

                  if (!productId) continue;

                  const data = getReleaseWindowFromProductId(productId);
                  if (data) {
                    matchedReleaseWindowLineCount += 1;
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const windowDates = releaseWindowDatesFor(data, multiBundle);
                    const customizationStarts = parseMonthDayStart(windowDates.customizationStarts, currentYear);
                    const customizationEnds = parseMonthDayEnd(windowDates.customizationEnds, currentYear);

                    if (customizationEnds < customizationStarts) {
                      customizationEnds.setFullYear(customizationEnds.getFullYear() + 1);
                    }

                    if (now >= customizationStarts && now <= customizationEnds) {
                      matchedReleaseWindowLineCountInsideWindow += 1;
                    }
                  }
                }
              }

              // Determine if editing is blocked
              if (matchedReleaseWindowLineCount > 0 && matchedReleaseWindowLineCount !== matchedReleaseWindowLineCountInsideWindow) {
                isEditingAllowed = false; // Outside release window
              }

              // CX-5123 — the 30-day "recently billed" check used to run here.
              // HALL asked for it to be replaced by the hold flag, which
              // subscriptionAllowsBoxEdit already applies above (isSubscriptionOnHold).
            }

            // PICKUP members are non-editable when disablePickupEdit is on.
            if (disablePickupEdit && isPickupMember()) {
              isEditingAllowed = false;
            }

            awtLog("list gate", subscriptionId, {
              hasEditBlockException,
              isEditingAllowed,
              variantId,
              pickup: isPickupMember()
            });

            // Only create and render button if editing is allowed
            if (!isEditingAllowed) {
              awtLog("list gate", subscriptionId, "-> Customize HIDDEN");
              return;
            }
            // The billing-attempts fetch above is async, so the list may have
            // re-rendered (or a second pass may have run) while it was in flight.
            if (!listItem.isConnected || listItem.querySelector("[data-bundle-variant-id]")) {
              awtLog("list gate", subscriptionId, "-> skipped (detached or already has a button)");
              return;
            }
            awtLog("list gate", subscriptionId, "-> Customize SHOWN");

            // Create new button without event listeners
            const clonedButton = document.createElement("button");
            clonedButton.className = button.className;
            clonedButton.innerHTML = "Customize";
            clonedButton.style.backgroundColor = "var(--primary-btn-color)";
            clonedButton.style.color = "var(--primary-btn-bg-color)";
            clonedButton.style.fontSize = "12px";
            clonedButton.style.padding = "0 11px";
            clonedButton.style.minWidth = "auto";

            // Add click event to save bundle VariantId and click Manage button
            clonedButton.addEventListener("click", () => {
              localStorage.setItem("editingBundleId", variantId);
              button.click();
            });

            clonedButton.setAttribute("data-bundle-variant-id", variantId);
            clonedButton.setAttribute("data-subscription-id", subscriptionId);

            const wrapper = document.createElement("div");
            wrapper.style.borderBottom = "var(--r1c2BorderBottom)";
            wrapper.appendChild(clonedButton);

            // Insert at 3rd position (index 2)
            const thirdChild = firstChildDiv.children[2];
            if (thirdChild) {
              firstChildDiv.insertBefore(wrapper, thirdChild);
            } else {
              firstChildDiv.appendChild(wrapper);
            }
          }
        }
      });
    };

    // Run now in case the list is already there, then keep watching while it
    // streams in. Items can mount, re-render, or have their Manage button filled in
    // at any point; scanList is cheap and every item is guarded, so repeat calls are
    // no-ops.
    let scanScheduled = false;
    const scheduleScan = () => {
      if (scanScheduled) return;
      scanScheduled = true;
      setTimeout(() => {
        scanScheduled = false;
        scanList();
      }, 100);
    };

    scanList();
    const listObserver = new MutationObserver(scheduleScan);
    listObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });

    // Safety valve: stop watching after this long so we don't hold a document-wide
    // observer for the life of the page. One last scan on the way out catches
    // anything that landed right on the boundary.
    const LIST_OBSERVER_TIMEOUT_MS = 7000;
    setTimeout(() => {
      listObserver.disconnect();
      scanList();
      awtLog("list gate: stopped watching for new list items after", LIST_OBSERVER_TIMEOUT_MS, "ms");
    }, LIST_OBSERVER_TIMEOUT_MS);
  }
})();
